import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { MapPin, Calendar, ArrowRight, ChevronRight, ChevronLeft, Search, X, UtensilsCrossed } from 'lucide-react';
import { getActivities, getChangemakers } from '../api/api';
import { scoreAndSortActivities, computeBaseScore, todayYMD } from '../utils/dates';
import { track, setCurrentPage } from '../utils/analytics';
import { RECYCLE_ICON, DONATE_ITEMS, DONATE_MODES, BLURU_CENTER, BLURU_BOUNDS } from '../utils/donateConstants';

const BENGALURU_IMAGE =
  'https://customer-assets-gfyr7b9c.emergentagent.net/job_76ed5a9e-c43a-4b4d-8d60-7589e627c0d8/artifacts/ysy1491k_Bengaluru%20Image.jpg';

const VIDEO_EXTS = ['.mp4', '.mov', '.avi', '.wmv', '.webm', '.mkv', '.flv', '.m4v'];

// ── Helpers ────────────────────────────────────────────────────────────────

const getFirstImage = (activity) => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const makeAbs = (url) => (!url ? null : url.startsWith('http') ? url : base + url);
  const isVideo  = (url) => VIDEO_EXTS.some((ext) => url.toLowerCase().includes(ext));
  if (activity.image_url && !isVideo(activity.image_url)) return makeAbs(activity.image_url);
  const found = (activity.media_urls || []).find((u) => u && !isVideo(u));
  return found ? makeAbs(found) : null;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch { return dateStr; }
};

const fmtTime = (timeStr) => {
  if (!timeStr) return '';
  const [h, m] = timeStr.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
};

const EMPTY_SCORING_CTX = {
  sharedActivityId: null, sharedActivityTags: [], sharedActivityChangemakerId: null,
  utmSource: null, utmContent: null, utmTerm: null,
  userType: null, userLocation: null, userCoords: null,
  userInterestFreqMap: {}, likedChangemakerIds: [],
  prevInterestedChangemakerIds: [], interestNameToIdMap: {},
};

// ── Initial Avatar fallback ────────────────────────────────────────────────

const AVATAR_PALETTE = ['#16a34a','#2563eb','#7c3aed','#dc2626','#d97706','#0891b2','#0f766e','#be185d'];

function OrgLogo({ logo, name, size = 14 }) {
  if (logo) {
    return <img src={logo} alt="" style={{ width: size, height: size }} className="rounded-full object-contain bg-white flex-shrink-0" />;
  }
  const initial = name ? name[0].toUpperCase() : '?';
  const color   = AVATAR_PALETTE[(name || '').charCodeAt(0) % AVATAR_PALETTE.length];
  return (
    <div
      className="rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
      style={{ width: size, height: size, backgroundColor: color, fontSize: Math.floor(size * 0.55), lineHeight: 1 }}
    >
      {initial}
    </div>
  );
}

// ── Mini Activity Card  (image left · text right) ─────────────────

export function MiniActivityCard({ activity, organizerName, organizerLogo, index = 0, onClick }) {
  const imgUrl = getFirstImage(activity);
  const loc    = activity.location || {};
  const address = loc.locality || loc.address || '';
  const isOngoingNoDate = activity.is_ongoing && !activity.date;
  const dateDisplay = isOngoingNoDate
    ? (activity.date_end ? `Ongoing · Last: ${fmtDate(activity.date_end)}` : 'Ongoing')
    : (activity.date_end
        ? `${fmtDate(activity.date)} – ${fmtDate(activity.date_end)}`
        : fmtDate(activity.date));

  return (
    <div
      className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm flex flex-row w-full cursor-pointer"
      data-testid={`home-mini-card-${activity.id}`}
      onClick={onClick}
    >
      {/* image — fixed pixel width, padded top/bottom/left */}
      <div
        className="relative flex-shrink-0"
        style={{ width: 'clamp(92px, 27vw, 126px)', aspectRatio: '4/5' }}
      >
        <div className="absolute inset-0 pt-1.5 pb-1.5 pl-1.5">
          {imgUrl ? (
            <img
              src={imgUrl}
              alt={activity.name}
              loading={index < 2 ? 'eager' : 'lazy'}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              className="w-full h-full object-cover object-top rounded-md"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-green-100 to-emerald-200 flex items-center justify-center rounded-md">
              <span className="text-xl">🌱</span>
            </div>
          )}
        </div>
      </div>

      {/* Text — fills remaining space after image */}
      <div className="flex flex-col justify-start gap-1.5 flex-1 min-w-0 pl-3 pr-3 py-2.5">
        <p className="text-sm font-bold text-gray-900 line-clamp-3 leading-snug min-h-[4.125em]">{activity.name}</p>
        {organizerName && (
          <div className="flex items-center gap-1">
            <OrgLogo logo={organizerLogo} name={organizerName} size={14} />
            <p className="text-[10px] text-gray-700 font-semibold line-clamp-1 leading-snug">{organizerName}</p>
          </div>
        )}
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <Calendar size={10} className={`flex-shrink-0 ${isOngoingNoDate ? 'text-amber-500' : 'text-gray-400'}`} />
            <p className={`text-[10px] font-medium leading-snug line-clamp-1 min-w-0 ${isOngoingNoDate ? 'text-amber-700' : 'text-gray-700'}`}>
              {dateDisplay}{!isOngoingNoDate && activity.start_time ? ` · ${fmtTime(activity.start_time)}` : ''}
            </p>
          </div>
          {address && (
            <div className="flex items-start gap-1.5">
              <MapPin size={10} className="text-green-500 flex-shrink-0 mt-[1px]" />
              <p className="text-[10px] text-gray-700 font-medium leading-snug line-clamp-2 min-h-[2.75em] min-w-0">{address}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Animated Donate Word ───────────────────────────────────────────────────

function DonateWordCycle() {
  const [idx,   setIdx]   = useState(0);
  const [phase, setPhase] = useState('in');

  useEffect(() => {
    let t;
    if (phase === 'in')  t = setTimeout(() => setPhase('out'), 2200);
    else                 t = setTimeout(() => { setIdx((i) => (i + 1) % DONATE_ITEMS.length); setPhase('in'); }, 300);
    return () => clearTimeout(t);
  }, [phase]);

  const item = DONATE_ITEMS[idx];
  return (
    <span
      key={`${idx}-${phase}`}
      className={`inline-flex items-center gap-1 text-green-600 ${phase === 'in' ? 'donate-word-in' : 'donate-word-out'}`}
    >
      {item.word}
      {item.icons.map((icon, i) => (
        <img key={i} src={icon} alt="" style={{ width: '1em', height: '1em', verticalAlign: 'middle' }} className="inline-block" />
      ))}
    </span>
  );
}

const CUTLERY_BANK_IMAGE =
  'https://customer-assets-rejwkqb3.emergentagent.net/job_5d6246c5-b72b-48b9-87a3-65cceb865b80/artifacts/knzbrg3z_Cutlery%20Bank.webp';

const CUTLERY_IMAGES = [
  { src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5d6246c5-b72b-48b9-87a3-65cceb865b80/artifacts/fo5wel6b_Steel%20Plate.jpg',    alt: 'Steel Plate'   },
  { src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5d6246c5-b72b-48b9-87a3-65cceb865b80/artifacts/ovarei0a_Steel%20spoons.jpg',   alt: 'Steel Spoons'  },
  { src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5d6246c5-b72b-48b9-87a3-65cceb865b80/artifacts/7z4t6zvf_steel%20tumbler.webp', alt: 'Steel Tumbler' },
  { src: 'https://customer-assets-rejwkqb3.emergentagent.net/job_5d6246c5-b72b-48b9-87a3-65cceb865b80/artifacts/kjypxpn4_steel%20bowl.jpg',     alt: 'Steel Bowl'    },
];

// ── Animated Cutlery Image Cycle ───────────────────────────────────────────

function CutleryImageCycle() {
  const [idx,   setIdx]   = useState(0);
  const [phase, setPhase] = useState('in');

  useEffect(() => {
    let t;
    if (phase === 'in')  t = setTimeout(() => setPhase('out'), 2200);
    else                 t = setTimeout(() => { setIdx((i) => (i + 1) % CUTLERY_IMAGES.length); setPhase('in'); }, 300);
    return () => clearTimeout(t);
  }, [phase]);

  const item = CUTLERY_IMAGES[idx];
  return (
    <span
      key={`cutlery-${idx}-${phase}`}
      className={`inline-block ${phase === 'in' ? 'donate-word-in' : 'donate-word-out'}`}
    >
      <img
        src={item.src}
        alt={item.alt}
        style={{ width: '1.35em', height: '1.35em', verticalAlign: 'middle', objectFit: 'contain' }}
      />
    </span>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function HomePage() {
  const navigate = useNavigate();

  // Scroll state for activities section
  const scrollRef          = useRef(null);
  const carouselColTrackRef = useRef(-1);
  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [isAtEnd,        setIsAtEnd]        = useState(false);
  const [hasOverflow,    setHasOverflow]    = useState(true);

  // Donate state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [donateMode,         setDonateMode]         = useState([]);
  const [donateLocation,     setDonateLocation]     = useState('');
  const [searchCoords,       setSearchCoords]       = useState(null);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [showSuggestions,    setShowSuggestions]    = useState(false);
  const locationInputRef = useRef(null);
  const searchCoordsRef  = useRef(null);

  // Sustainability carousel
  const [currentSection,    setCurrentSection]    = useState(0);
  const [carouselTranslate, setCarouselTranslate] = useState(0);
  const carouselSectionRef  = useRef(null);
  const hasTriggeredPeekRef = useRef(false);
  const peekTimeoutsRef     = useRef([]);
  // swipe / drag refs
  const touchStartXRef      = useRef(null);
  const dragStartXRef       = useRef(null);
  const isDraggingRef       = useRef(false);

  // Cutlery location state
  const [cutleryLocation,        setCutleryLocation]        = useState('');
  const [cutleryCoords,          setCutleryCoords]          = useState(null);
  const [cutlerySuggestions,     setCutlerySuggestions]     = useState([]);
  const [showCutlerySuggestions, setShowCutlerySuggestions] = useState(false);
  const cutleryCoordsRef = useRef(null);

  useEffect(() => { setCurrentPage('home'); track('page_view', { page: 'home' }); }, []);

  // ── Data Fetching ──────────────────────────────────────────────────────
  const { data: activities = [], isLoading: activitiesLoading } = useQuery({
    queryKey: ['activities'],
    queryFn: async () => (await getActivities({})).data,
  });

  const { data: changemakersData = [] } = useQuery({
    queryKey: ['changemakers'],
    queryFn: async () => (await getChangemakers()).data,
  });

  const changemakerMap = useMemo(() => {
    const map = {};
    changemakersData.forEach((cm) => { map[cm.id] = cm; });
    return map;
  }, [changemakersData]);

  const getChangemakerName = useCallback((id) => changemakerMap[id]?.name || null, [changemakerMap]);
  const getChangemakerLogo = useCallback((id) => {
    const cm = changemakerMap[id];
    if (!cm) return null;
    const raw = cm.logo_url || cm.image_url || null;
    if (!raw) return null;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return raw.startsWith('http') ? raw : base + raw;
  }, [changemakerMap]);

  const topActivities = useMemo(() => {
    if (!activities.length) return [];
    try {
      const sorted = scoreAndSortActivities(activities, EMPTY_SCORING_CTX, { noConstraints: false });
      // Separate upcoming (total_base_score >= 0) from past (total_base_score < 0)
      const upcoming = sorted.filter((a) => computeBaseScore(a).total_base_score >= 0);
      const past     = sorted.filter((a) => computeBaseScore(a).total_base_score < 0);
      // Minimum 3 columns; grow to fit all upcoming activities
      const numCols    = Math.max(3, Math.ceil(upcoming.length / 3));
      const totalSlots = numCols * 3;
      // Upcoming first (score-sorted), then past fills any remaining slots
      return [...upcoming, ...past].slice(0, totalSlots);
    } catch { return activities.slice(0, 9); }
  }, [activities]);

  const columns = useMemo(() => {
    const cols = [];
    for (let i = 0; i < topActivities.length; i += 3) cols.push(topActivities.slice(i, i + 3));
    return cols;
  }, [topActivities]);

  // ── Scroll handlers ────────────────────────────────────────────────────
  const checkScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setIsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 20);
    setHasOverflow(el.scrollWidth > el.clientWidth + 20);
  }, []);

  useEffect(() => {
    checkScrollState();
    window.addEventListener('resize', checkScrollState);
    return () => window.removeEventListener('resize', checkScrollState);
  }, [columns, checkScrollState]);

  const scrollRight = () => { track('home_carousel_arrow', { direction: 'next', scroll_column_before: Math.round((scrollRef.current?.scrollLeft || 0) / (scrollRef.current?.clientWidth || 1)), section: 'activity_carousel' }); scrollRef.current?.scrollBy({ left: scrollRef.current.clientWidth * 0.82, behavior: 'smooth' }); };
  const scrollLeft  = () => { track('home_carousel_arrow', { direction: 'prev', scroll_column_before: Math.round((scrollRef.current?.scrollLeft || 0) / (scrollRef.current?.clientWidth || 1)), section: 'activity_carousel' }); scrollRef.current?.scrollBy({ left: -scrollRef.current.clientWidth * 0.82, behavior: 'smooth' }); };

  const handleCarouselScroll = useCallback(() => {
    checkScrollState();
    const el = scrollRef.current;
    if (!el || !el.clientWidth) return;
    const newCol = Math.round(el.scrollLeft / el.clientWidth);
    if (newCol !== carouselColTrackRef.current) {
      carouselColTrackRef.current = newCol;
      track('home_carousel_scroll', { scroll_column: newCol, section: 'activity_carousel' });
    }
  }, [checkScrollState]);

  const fetchPlaceSuggestions = async (input) => {
    if (!input || input.length < 2 || !window.google?.maps?.places?.AutocompleteSuggestion) { setLocationSuggestions([]); return; }
    try {
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        locationRestriction: BLURU_BOUNDS,
      });
      setLocationSuggestions(suggestions.map((s) => ({ text: s.placePrediction.text.toString(), prediction: s.placePrediction })));
      setShowSuggestions(true);
    } catch { setLocationSuggestions([]); }
  };

  const handleLocationChange = (e) => { const v = e.target.value; setDonateLocation(v); if (v) fetchPlaceSuggestions(v); else setLocationSuggestions([]); };

  const clearLocation = () => {
    setDonateLocation('');
    setLocationSuggestions([]);
    setShowSuggestions(false);
    setSearchCoords(null);
    searchCoordsRef.current = null;
  };

  const selectSuggestion = async (item) => {
    setDonateLocation(item.text);
    setLocationSuggestions([]);
    setShowSuggestions(false);
    try {
      const place = item.prediction.toPlace();
      await place.fetchFields({ fields: ['location'] });
      const loc = place.location;
      if (loc) {
        const coords = { lat: loc.lat(), lng: loc.lng() };
        searchCoordsRef.current = coords;
        setSearchCoords(coords);
      }
    } catch { /* silent */ }
  };

  // ── Donate helpers ─────────────────────────────────────────────────────
  // Toggle category by slug
  const toggleCategory = (slug) => {
    const isActive = selectedCategories.includes(slug);
    const newCats = isActive ? selectedCategories.filter(s => s !== slug) : [...selectedCategories, slug];
    track('donate_category_click', { category_slug: slug, action: isActive ? 'deselect' : 'select', active_categories: newCats });
    setSelectedCategories(newCats);
  };

  const handleFindDonate = () => {
    track('engagement_action', { action: 'find_donate_click', categories: selectedCategories, mode: donateMode, location: donateLocation });
    const params = new URLSearchParams();
    if (selectedCategories.length) params.set('service_types', selectedCategories.join(','));
    if (donateMode.length) {
      const mapped = donateMode.map(m => m === 'Pick Up' ? 'pickup' : 'dropoff');
      params.set('mode', mapped.join(','));
    }
    if (donateLocation) params.set('address', donateLocation);
    if (searchCoords) { params.set('lat', String(searchCoords.lat)); params.set('lng', String(searchCoords.lng)); }
    params.set('autoSearch', '1');
    navigate(`/services/donate-used-items?${params.toString()}`);
  };

  // ── Carousel helpers ───────────────────────────────────────────────────
  const goToSection = useCallback((idx) => {
    peekTimeoutsRef.current.forEach(clearTimeout);
    peekTimeoutsRef.current = [];
    setCurrentSection(idx);
    setCarouselTranslate(idx * 100);
  }, []);

  // Swipe (touch) and drag (mouse) handlers
  const onSwipeEnd = useCallback((endX, startX) => {
    if (startX === null) return;
    const delta = startX - endX;
    if (Math.abs(delta) > 48) {
      if (delta > 0 && currentSection < 1) goToSection(currentSection + 1);
      else if (delta < 0 && currentSection > 0) goToSection(currentSection - 1);
    }
  }, [currentSection, goToSection]);

  const carouselTouchStart  = (e) => { touchStartXRef.current = e.touches[0].clientX; };
  const carouselTouchEnd    = (e) => { onSwipeEnd(e.changedTouches[0].clientX, touchStartXRef.current); touchStartXRef.current = null; };
  const carouselMouseDown   = (e) => { dragStartXRef.current = e.clientX; isDraggingRef.current = true; };
  const carouselMouseUp     = (e) => { if (isDraggingRef.current) { onSwipeEnd(e.clientX, dragStartXRef.current); } isDraggingRef.current = false; dragStartXRef.current = null; };
  const carouselMouseLeave  = ()  => { isDraggingRef.current = false; dragStartXRef.current = null; };

  // Sneak-peek on first scroll-into-view
  useEffect(() => {
    const el = carouselSectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTriggeredPeekRef.current) {
          hasTriggeredPeekRef.current = true;
          const t1 = setTimeout(() => setCarouselTranslate(22), 700);
          const t2 = setTimeout(() => setCarouselTranslate(0),  2000);
          peekTimeoutsRef.current = [t1, t2];
          observer.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Cutlery location helpers ────────────────────────────────────────────
  const fetchCutlerySuggestions = async (input) => {
    if (!input || input.length < 2 || !window.google?.maps?.places?.AutocompleteSuggestion) { setCutlerySuggestions([]); return; }
    try {
      const { suggestions } = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input, locationRestriction: BLURU_BOUNDS,
      });
      setCutlerySuggestions(suggestions.map((s) => ({ text: s.placePrediction.text.toString(), prediction: s.placePrediction })));
      setShowCutlerySuggestions(true);
    } catch { setCutlerySuggestions([]); }
  };

  const handleCutleryLocationChange = (e) => {
    const v = e.target.value;
    setCutleryLocation(v);
    if (v) fetchCutlerySuggestions(v); else setCutlerySuggestions([]);
  };

  const clearCutleryLocation = () => {
    setCutleryLocation('');
    setCutlerySuggestions([]);
    setShowCutlerySuggestions(false);
    setCutleryCoords(null);
    cutleryCoordsRef.current = null;
  };

  const selectCutlerySuggestion = async (item) => {
    setCutleryLocation(item.text);
    setCutlerySuggestions([]);
    setShowCutlerySuggestions(false);
    try {
      const place = item.prediction.toPlace();
      await place.fetchFields({ fields: ['location'] });
      const loc = place.location;
      if (loc) {
        const coords = { lat: loc.lat(), lng: loc.lng() };
        cutleryCoordsRef.current = coords;
        setCutleryCoords(coords);
      }
    } catch { /* silent */ }
  };

  const handleFindCutlery = () => {
    track('engagement_action', { action: 'find_cutlery_click', location: cutleryLocation });
    const params = new URLSearchParams();
    if (cutleryLocation) params.set('address', cutleryLocation);
    const coords = cutleryCoordsRef.current;
    if (coords) {
      params.set('lat', String(coords.lat));
      params.set('lng', String(coords.lng));
    }
    params.set('autoSearch', '1');
    navigate(`/cutlery-bank?${params.toString()}`);
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Layout>

      {/* ══════════════════════════════════════════════════════════════════
          HERO  (66vh)
      ══════════════════════════════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ height: '66vh', minHeight: 340 }}
        data-testid="hero-section"
      >
        <img
          src={BENGALURU_IMAGE}
          alt="Bengaluru — Vidhana Soudha"
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: '50% 80%' }}
        />
        {/* Gradient scrim — top 55% only so building is fully visible */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.52) 38%, rgba(0,0,0,0) 62%)' }}
        />

        {/* Text — centered, anchored to top */}
        <div className="relative z-10 px-5 pt-7 pb-0 max-w-2xl mx-auto text-center">
          <h1
            className="font-extrabold text-white text-center leading-tight tracking-tight drop-shadow-lg"
            style={{ fontSize: 'clamp(1.9rem, 6.8vw, 3.8rem)', textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}
            data-testid="hero-heading"
          >
            Be A Part of Making<br />Bengaluru Better
          </h1>

          <p
            className="mt-3 text-white/90 text-center font-medium leading-relaxed"
            style={{ fontSize: 'clamp(0.78rem, 2.6vw, 1rem)', textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}
          >
            Join Activities like Cleanup Drives. Lake Restorations. Tree Plantations.
            Meetups &amp; Conversations and much more
          </p>

          <p
            className="mt-2 text-white/55 text-center font-semibold uppercase tracking-widest"
            style={{ fontSize: 'clamp(0.55rem, 1.5vw, 0.7rem)', textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}
          >
            Organised by Changemakers, NGOs, Citizen Groups, Youth Groups and RWAs
          </p>

          <div className="flex justify-center mt-4">
            <Link
              to="/activities"
              className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-green-600 hover:bg-green-500 text-white font-semibold text-sm shadow-lg transition-all duration-200 active:scale-95"
              data-testid="hero-see-activities-btn"
              onClick={() => track('engagement_action', { action: 'hero_see_activities' })}
            >
              See Activities
              <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════
          ACTIVITIES SECTION
      ══════════════════════════════════════════════════════════════════ */}
      <div className="h-2.5 bg-gray-100 border-y border-gray-200" />
      <section className="bg-gray-50 pt-5 pb-5" data-testid="home-activities-section">

        {/* Heading row */}
        <div className="px-4 max-w-xl mx-auto mb-3 flex items-center justify-between gap-2">
          <h2
            className="font-bold text-gray-900 leading-tight"
            style={{ fontSize: 'clamp(1.35rem, 5vw, 1.85rem)' }}
            data-testid="activities-section-heading"
          >
            Participate in Civic Activities
          </h2>
          <Link
            to="/activities"
            className="flex-shrink-0 px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
            data-testid="activities-see-all-inline"
            onClick={() => track('engagement_action', { action: 'home_see_all_heading' })}
          >
            <span className="text-xs font-semibold text-gray-400 whitespace-nowrap">Show All</span>
          </Link>
        </div>

        {/* Scroll container + arrow markers */}
        <div className="max-w-xl mx-auto overflow-hidden">
        <div className="relative">

          {/* Left arrow */}
          {canScrollLeft && (
            <button
              onClick={scrollLeft}
              className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-white/95 rounded-full shadow-md flex items-center justify-center hover:bg-white hover:shadow-lg transition-all"
              data-testid="scroll-left-btn"
              aria-label="Scroll left"
            >
              <ChevronLeft size={20} className="text-gray-700" />
            </button>
          )}

          {/* Scroll area */}
          <div
            ref={scrollRef}
            onScroll={handleCarouselScroll}
            className="overflow-x-auto scrollbar-hide px-3"
            data-testid="activities-scroll-container"
          >
            {activitiesLoading ? (
              /* Skeleton: 2 columns × 3 cards while activities load */
              <div className="flex gap-3 sm:gap-4 pb-1">
                {[0, 1].map((colIdx) => (
                  <div
                    key={colIdx}
                    className="flex flex-col gap-2.5 flex-shrink-0"
                    style={{ width: 'clamp(210px, 72vw, 300px)' }}
                  >
                    {[0, 1, 2].map((j) => (
                      <div
                        key={j}
                        className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-row overflow-hidden animate-pulse"
                      >
                        <div
                          className="bg-gray-200 flex-shrink-0"
                          style={{ width: 'clamp(92px, 27vw, 126px)', aspectRatio: '4/5' }}
                        />
                        <div className="flex-1 px-3 py-2.5 flex flex-col gap-2 justify-center">
                          <div className="bg-gray-200 rounded h-2.5 w-3/4" />
                          <div className="bg-gray-200 rounded h-2 w-1/2" />
                          <div className="bg-gray-200 rounded h-2 w-2/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex gap-3 sm:gap-4 pb-1" style={{ width: 'max-content' }}>
                {columns.map((col, colIdx) => (
                  <div
                    key={colIdx}
                    className="flex flex-col gap-2.5 flex-shrink-0"
                    style={{ width: 'clamp(210px, 72vw, 300px)' }}
                    data-testid={`activity-column-${colIdx}`}
                  >
                    {col.map((activity, rowIdx) => (
                      <Link
                        key={activity.id}
                        to={`/activities?scrollTo=${activity.id}&activity_id=${activity.id}`}
                        className="block"
                        data-testid={`home-mini-card-link-${activity.id}`}
                        onClick={() => track('home_activity_click', {
                          activity_id: activity.id,
                          activity_name: activity.name,
                          activity_position: colIdx * 3 + rowIdx,
                          activity_date: activity.date || null,
                          activity_location_locality: activity.location?.locality || null,
                          activity_interest_tags: activity.areas_of_interest || [],
                          organisation_id: activity.changemaker_id || null,
                          organisation_name: getChangemakerName(activity.changemaker_id) || null,
                          carousel_column: colIdx,
                          carousel_row: rowIdx,
                          section: 'activity_carousel',
                        })}
                      >
                        <MiniActivityCard
                          activity={activity}
                          organizerName={getChangemakerName(activity.changemaker_id) || activity.other_organizer_name || null}
                          organizerLogo={getChangemakerLogo(activity.changemaker_id)}
                          index={colIdx}
                        />
                      </Link>
                    ))}
                  </div>
                ))}

                {/* Inline Show All column — always visible at the end of the scroll */}
                <div
                  className="flex-shrink-0 flex flex-col items-center justify-center"
                  style={{ width: 'clamp(80px, 20vw, 110px)' }}
                  data-testid="see-more-inline-col"
                >
                  <Link
                    to="/activities"
                    className="px-3 py-1.5 bg-white rounded-lg border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors"
                    data-testid="see-more-inline-btn"
                    onClick={() => track('engagement_action', { action: 'home_see_more_inline' })}
                  >
                    <span className="text-xs font-semibold text-gray-400 whitespace-nowrap">Show All</span>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Right arrow — only while there is more to scroll */}
          {hasOverflow && !isAtEnd && (
            <button
              onClick={scrollRight}
              className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 z-20 w-9 h-9 bg-white/95 rounded-full shadow-md flex items-center justify-center hover:bg-white hover:shadow-lg transition-all"
              data-testid="scroll-right-btn"
              aria-label="Scroll right"
            >
              <ChevronRight size={20} className="text-gray-700" />
            </button>
          )}
        </div>
        </div>

        {/* Bottom CTA */}
        <div className="px-4 max-w-xl mx-auto mt-4">
          <Link
            to="/activities"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors active:scale-95"
            data-testid="see-all-activities-btn"
            onClick={() => track('engagement_action', { action: 'home_see_all_btn' })}
          >
            <span className="text-xs font-semibold text-gray-400 whitespace-nowrap">Show All Activities</span>
          </Link>
        </div>
      </section>

      {/* Section divider */}
      <div className="h-2.5 bg-gray-100 border-y border-gray-200" />

      {/* ══════════════════════════════════════════════════════════════════
          SUSTAINABILITY CAROUSEL
      ══════════════════════════════════════════════════════════════════ */}
      <section ref={carouselSectionRef} className="bg-white" data-testid="sustainability-carousel-section">
        <div className="max-w-xl mx-auto">

        {/* ── Green ribbon ─────────────────────────────── */}
        <div className="flex items-center bg-green-600" style={{ height: '2.1rem' }}>

          {/* Fixed left: back arrow (when on cutlery) + "Explore Sustainable Living" */}
          <div className="flex-shrink-0 flex items-center h-full pl-3 pr-2 gap-1">
            {currentSection > 0 && (
              <button
                onClick={() => { track('service_carousel_nav', { direction: 'prev', section_after: currentSection - 1 }); goToSection(currentSection - 1); }}
                data-testid="carousel-ribbon-prev"
                className="text-white/80 hover:text-white transition-colors"
                aria-label="Previous section"
              >
                <ChevronLeft size={13} />
              </button>
            )}
            <span className="text-white font-semibold whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
              Explore Sustainable Living
            </span>
          </div>

          {/* Subtle separator */}
          <span className="text-white/30 flex-shrink-0 text-xs select-none">|</span>

          {/* Scrolling middle text */}
          <div className="flex-1 overflow-hidden min-w-0 px-2">
            <div className="marquee-track text-white/90 font-medium" style={{ fontSize: '0.7rem', letterSpacing: '0.01em' }}>
              <span className="pr-10">Donate Used Items.&nbsp;&nbsp;Find Cutlery Bank.&nbsp;&nbsp;Zero Waste Events.</span>
              <span className="pr-10">Donate Used Items.&nbsp;&nbsp;Find Cutlery Bank.&nbsp;&nbsp;Zero Waste Events.</span>
            </div>
          </div>

          {/* Subtle separator + fixed right: "Show more →" */}
          {currentSection < 1 && (
            <>
              <span className="text-white/30 flex-shrink-0 text-xs select-none">|</span>
              <button
                onClick={() => { track('service_carousel_nav', { direction: 'next', section_after: currentSection + 1 }); goToSection(currentSection + 1); }}
                data-testid="carousel-ribbon-next"
                className="flex-shrink-0 flex items-center gap-1 h-full pl-2 pr-3 text-white hover:text-white/80 transition-colors"
                aria-label="Next section"
              >
                <span className="font-medium whitespace-nowrap" style={{ fontSize: '0.7rem' }}>Show more</span>
                <ChevronRight size={13} />
              </button>
            </>
          )}

        </div>

        {/* ── Carousel track ────────────────────────────────────────── */}
        <div
          className="overflow-hidden w-full select-none cursor-grab active:cursor-grabbing"
          onTouchStart={carouselTouchStart}
          onTouchEnd={carouselTouchEnd}
          onMouseDown={carouselMouseDown}
          onMouseUp={carouselMouseUp}
          onMouseLeave={carouselMouseLeave}
          onDragStart={(e) => e.preventDefault()}
        >
          <div
            className="flex"
            style={{
              transform: `translateX(-${carouselTranslate}%)`,
              transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1)',
            }}
            data-testid="carousel-track"
          >

            {/* ── Slide 1: Donate ───────────────────────────────────── */}
            <div className="w-full flex-shrink-0 flex flex-col" data-testid="carousel-slide-donate">
              <div className="pt-7 pb-5 px-4 flex flex-col flex-1">
                <div className="max-w-xl mx-auto w-full flex flex-col flex-1">

                  {/* Heading row */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-3">
                      <img src={RECYCLE_ICON} alt="Recycle" style={{ width: '2.4rem', height: '2.4rem', flexShrink: 0 }} />
                      <h2
                        className="font-bold text-gray-900 leading-snug"
                        style={{ fontSize: 'clamp(1.25rem, 5vw, 1.75rem)' }}
                        data-testid="donate-heading"
                      >
                        <span className="block">Donate Your Used</span>
                        <span className="inline-flex overflow-hidden align-bottom" style={{ height: '1.4em', verticalAlign: 'bottom' }}>
                          <DonateWordCycle />
                        </span>
                      </h2>
                    </div>
                    <Link
                      to="/services/donate-used-items"
                      data-testid="donate-show-more-btn"
                      onClick={() => track('service_show_more_click', { service_type: 'donate' })}
                      className="flex-shrink-0 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-xs font-semibold text-gray-600 whitespace-nowrap transition-colors"
                    >
                      Show More
                    </Link>
                  </div>
                  <p className="text-gray-500 text-sm mb-5 pl-1">
                    Give your items a second life. Conveniently dispose them through pickups or drop offs. Find multiple options and choose what suits you best.
                  </p>

                  {/* Category multi-select */}
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      What would you like to donate?
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {DONATE_ITEMS.map(({ cat, slug, icons }) => {
                        const active = selectedCategories.includes(slug);
                        return (
                          <button
                            key={slug}
                            onClick={() => toggleCategory(slug)}
                            data-testid={`donate-cat-${slug}`}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium border transition-all duration-150 active:scale-95 ${
                              active
                                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-700'
                            }`}
                          >
                            <span className="flex items-center gap-0.5">
                              {icons.map((icon, i) => (
                                <img key={i} src={icon} alt="" style={{ width: 18, height: 18 }} className="flex-shrink-0" />
                              ))}
                            </span>
                            {cat}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Donate mode selection */}
                  <div className="mb-5">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      How would you like to donate?
                    </p>
                    <div className="flex gap-2">
                      {DONATE_MODES.map((mode) => {
                        const active = donateMode.includes(mode);
                        return (
                          <button
                            key={mode}
                            onClick={() => { const newModes = active ? donateMode.filter(m => m !== mode) : [...donateMode, mode]; track('donate_mode_click', { mode, action: active ? 'deselect' : 'select', active_modes: newModes }); setDonateMode(newModes); }}
                            data-testid={`donate-mode-${mode.toLowerCase().replace(/\s+/g, '-')}`}
                            className={`flex-1 py-2 rounded-full text-sm font-medium border transition-all duration-150 active:scale-95 ${
                              active
                                ? 'bg-green-600 text-white border-green-600 shadow-sm'
                                : 'bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-700'
                            }`}
                          >
                            {mode}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Location + CTA — pushed to bottom so both slides align */}
                  <div className="mt-auto">
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Where are you?
                      </p>
                      <div className="relative">
                        <MapPin size={16} className="absolute left-3 top-3.5 text-green-600 pointer-events-none z-10" />
                        <input
                          ref={locationInputRef}
                          type="text"
                          placeholder="Type your area or locality…"
                          value={donateLocation}
                          onChange={handleLocationChange}
                          onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                          data-testid="donate-location-input"
                          autoComplete="off"
                          className="w-full pl-9 pr-9 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                        {donateLocation && (
                          <button onClick={clearLocation} data-testid="donate-location-clear"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Clear location">
                            <X size={15} />
                          </button>
                        )}
                        {showSuggestions && locationSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 overflow-hidden">
                            {locationSuggestions.slice(0, 5).map((item, i) => (
                              <button key={i} onMouseDown={() => selectSuggestion(item)}
                                className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                <MapPin size={12} className="text-green-500 flex-shrink-0" />
                                <span className="line-clamp-1">{item.text}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleFindDonate}
                      data-testid="find-donate-btn"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-green-200 hover:bg-green-300 text-green-800 font-semibold text-sm shadow-sm transition-all duration-150 active:scale-95"
                    >
                      <Search size={16} />
                      Find Places to Donate
                    </button>
                  </div>

                </div>
              </div>
            </div>

            {/* ── Slide 2: Rent Steel Cutlery ───────────────────────── */}
            <div className="w-full flex-shrink-0 flex flex-col" data-testid="carousel-slide-cutlery">
              <div className="flex-1 pt-7 pb-5 px-4 flex flex-col">
                <div className="max-w-xl mx-auto w-full flex flex-col flex-1">

                  {/* Heading row */}
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-3">
                      <UtensilsCrossed size={38} className="text-green-600 flex-shrink-0" />
                      <h2
                        className="font-bold text-gray-900 leading-snug"
                        style={{ fontSize: 'clamp(1.25rem, 5vw, 1.75rem)' }}
                        data-testid="cutlery-heading"
                      >
                        Rent Steel Cutlery{' '}
                        <span className="inline-flex overflow-hidden align-bottom" style={{ height: '1.6em', verticalAlign: 'bottom' }}>
                          <CutleryImageCycle />
                        </span>
                      </h2>
                    </div>
                    <Link
                      to="/cutlery-bank"
                      data-testid="cutlery-show-more-btn"
                      onClick={() => track('service_show_more_click', { service_type: 'cutlery' })}
                      className="flex-shrink-0 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-xs font-semibold text-gray-600 whitespace-nowrap transition-colors"
                    >
                      Show More
                    </Link>
                  </div>

                  {/* Subheading */}
                  <p className="text-gray-500 text-sm mb-4 pl-1">
                    Reduce Waste Generation. Instead of using disposables, use steel cutlery for any event or gathering. Available for nominal rent and even for free. Use, wash and return. This prevents lots of disposables from becoming waste. Find the nearest Cutlery Bank.
                  </p>

                  {/* Cutlery Bank image — fixed height, smaller */}
                  <div className="h-36 rounded-xl overflow-hidden mb-4">
                    <img
                      src={CUTLERY_BANK_IMAGE}
                      alt="Cutlery Bank"
                      className="w-full h-full object-cover"
                    />
                  </div>

                  {/* Location + CTA — pushed to bottom to align with Donate slide */}
                  <div className="mt-auto">
                    <div className="mb-5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        Where are you?
                      </p>
                      <div className="relative">
                        <MapPin size={16} className="absolute left-3 top-3.5 text-green-600 pointer-events-none z-10" />
                        <input
                          type="text"
                          placeholder="Type your area or locality…"
                          value={cutleryLocation}
                          onChange={handleCutleryLocationChange}
                          onFocus={() => cutlerySuggestions.length > 0 && setShowCutlerySuggestions(true)}
                          onBlur={() => setTimeout(() => setShowCutlerySuggestions(false), 150)}
                          data-testid="cutlery-location-input"
                          autoComplete="off"
                          className="w-full pl-9 pr-9 py-3 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                        />
                        {cutleryLocation && (
                          <button onClick={clearCutleryLocation} data-testid="cutlery-location-clear"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Clear location">
                            <X size={15} />
                          </button>
                        )}
                        {showCutlerySuggestions && cutlerySuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 overflow-hidden">
                            {cutlerySuggestions.slice(0, 5).map((item, i) => (
                              <button key={i} onMouseDown={() => selectCutlerySuggestion(item)}
                                className="w-full text-left px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 border-b border-gray-100 last:border-0 flex items-center gap-2">
                                <MapPin size={12} className="text-green-500 flex-shrink-0" />
                                <span className="line-clamp-1">{item.text}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={handleFindCutlery}
                      data-testid="find-cutlery-btn"
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-green-200 hover:bg-green-300 text-green-800 font-semibold text-sm shadow-sm transition-all duration-150 active:scale-95"
                    >
                      <Search size={16} />
                      Find Cutlery Bank
                    </button>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ── Dot indicators ────────────────────────────────────────── */}
        <div className="flex justify-center gap-3 py-4 pb-6" data-testid="carousel-dots">
          {[0, 1].map((idx) => (
            <button
              key={idx}
              onClick={() => { track('service_carousel_dot', { section_after: idx }); goToSection(idx); }}
              data-testid={`carousel-dot-${idx}`}
              className={`rounded-full transition-all duration-300 ${
                currentSection === idx
                  ? 'w-5 h-2.5 bg-green-600'
                  : 'w-2.5 h-2.5 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to section ${idx + 1}`}
            />
          ))}
        </div>

        </div>{/* end max-w-xl mx-auto */}
      </section>

    </Layout>
  );
}
